import { useEffect, useMemo, useRef } from "react"

// The indicator stays up this long after the last keystroke, so a pause to
// think does not blink it away.
export const TYPING_IDLE_MS = 10_000
// Keystrokes only re-announce this often; the receiver's expiry outlives it.
export const TYPING_HEARTBEAT_MS = 2_000

interface TypingEmitterOptions {
	idleMs?: number
	heartbeatMs?: number
}

// Announces typing on a heartbeat and sends one stop after the sender goes
// idle, so a stale stop never hides the indicator mid-typing.
export function createTypingEmitter(
	setTyping: (isTyping: boolean) => void,
	{
		idleMs = TYPING_IDLE_MS,
		heartbeatMs = TYPING_HEARTBEAT_MS,
	}: TypingEmitterOptions = {},
) {
	let stopTimer: ReturnType<typeof setTimeout> | null = null
	let announcedAt: number | null = null

	const cancel = () => {
		if (stopTimer) {
			clearTimeout(stopTimer)
			stopTimer = null
		}
		announcedAt = null
	}

	const notifyTyping = () => {
		const now = Date.now()
		if (announcedAt === null || now - announcedAt >= heartbeatMs) {
			announcedAt = now
			setTyping(true)
		}
		if (stopTimer) clearTimeout(stopTimer)
		stopTimer = setTimeout(() => {
			stopTimer = null
			announcedAt = null
			setTyping(false)
		}, idleMs)
	}

	// Sending is the one moment the indicator must drop instantly instead of
	// waiting out the idle window.
	const stopTyping = () => {
		const wasAnnounced = announcedAt !== null
		cancel()
		if (wasAnnounced) setTyping(false)
	}

	return { notifyTyping, stopTyping, cancel }
}

export function useTypingEmitter(
	setTyping: (isTyping: boolean) => void,
	options?: TypingEmitterOptions,
) {
	const setTypingRef = useRef(setTyping)
	setTypingRef.current = setTyping

	const idleMs = options?.idleMs
	const heartbeatMs = options?.heartbeatMs
	const emitter = useMemo(
		() =>
			createTypingEmitter((isTyping) => setTypingRef.current(isTyping), {
				idleMs,
				heartbeatMs,
			}),
		[idleMs, heartbeatMs],
	)

	useEffect(() => () => emitter.cancel(), [emitter])

	return emitter
}
